import { SectionCard, fmtTRY } from '../yeni-design/primitives';
import type { Donem } from './donem';
import type { SaKaynak } from './ekranKaynaklari';
import { useSaSorgu } from './useSatisVerisi';
import { BlokIskeleti } from './BlokIskeleti';
import { miktarMetni, oranMetni } from './bicim';
import { sayi, type Sayisal } from './ozetHesaplari';

interface CokSatanSatiri {
  stok_kodu: string;
  urun_adi: string;
  miktar: Sayisal;
  ciro: Sayisal;
  kar: Sayisal;
}

/**
 * FINAL-FIX F-2: ekranin TEK marj tanimi kar ÷ URUN cirosu (KDV dahil `ciro`; spec §2.5, kâr karti,
 * KategoriBlok, Uyarilar, KarKoprusu). 990053'un `kar_marji` kolonu kar ÷ KDV HARIC tutar — ayni ekranda
 * ikinci bir "Marj" olurdu, OKUNMAZ. Ciro 0 ya da kar yoksa (kart maliyeti NULL) null.
 */
export function urunMarji(r: Pick<CokSatanSatiri, 'ciro' | 'kar'>): number | null {
  const ciro = sayi(r.ciro);
  if (ciro === 0 || r.kar === null || r.kar === undefined) return null;
  return (sayi(r.kar) / ciro) * 100;
}

interface Props {
  kaynak: SaKaynak | undefined;
  donem: Donem;
  sube?: string | null;
  kanal?: string | null;
  /** K7 (G2b): urun adi dugmesi urun detayini (M6) acar. 990053 `stok_kodu` tasir (SQL 137:126). */
  onUrunSec?: (u: { stokKodu: string; urunAdi: string }) => void;
  /** Urun sekmesi: raporun verdigi ilk 20, Miktar ve Kar kolonlariyla. Verilmezse ilk 5. */
  ayrintili?: boolean;
  /** Verilirse baslikta "Tüm ürünler" dugmesi: sayfadaki detay penceresini acar. */
  onTumunuAc?: () => void;
}

export const COK_SATAN_ILK_N = 5;

/**
 * S5 (spec §9): kirmizi renk GOSTERILEN (yuvarlanmis) degerle TUTARLI olsun — cok kucuk negatif marj
 * yuvarlaninca "%0,00" gorunurken kirmizi boyanmasin (oranMetni'nin isaret kuraliyla ayni mantik).
 */
function marjGoruntudeNegatifMi(marj: number): boolean {
  return marj < 0 && Number(Math.abs(marj).toFixed(2)) !== 0;
}

/** Satir 3 — en cok satan 5 (990053; ds ciro azalan ilk 20 dondurur, sirasi korunur). */
export function CokSatanBlok({ kaynak, donem, sube = null, kanal = null, onUrunSec, ayrintili = false, onTumunuAc }: Props) {
  const q = useSaSorgu<CokSatanSatiri>(kaynak, { donem, sube, kanal });

  if (!kaynak) return null;
  if (q.isLoading) return <BlokIskeleti testid="blok-cok-satan-iskelet" baslik="En çok satan" yukseklik={180} satir={5} />;
  const satirlar = ayrintili ? (q.data?.data ?? []) : (q.data?.data ?? []).slice(0, COK_SATAN_ILK_N);
  if (satirlar.length === 0) return null;

  return (
    <div data-testid="blok-cok-satan">
      <SectionCard
        title="En çok satan"
        subtitle={`Ciroya göre ilk ${satirlar.length}`}
        actions={
          onTumunuAc && (
            <button
              type="button"
              data-testid="cok-satan-tumu"
              onClick={onTumunuAc}
              className="focus-ring rounded-md border border-border px-2.5 py-1 text-[12px] text-ink-700 hover:text-ink-900"
            >
              Tüm ürünler
            </button>
          )
        }
      >
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="text-ink-400 text-left">
              <th className="font-normal py-1">Ürün</th>
              {ayrintili && <th className="font-normal py-1 pl-2 text-right">Miktar</th>}
              <th className="font-normal py-1 pl-2 text-right">Ciro</th>
              {ayrintili && <th className="font-normal py-1 pl-2 text-right">Kâr</th>}
              <th className="font-normal py-1 pl-2 text-right">Marj</th>
            </tr>
          </thead>
          <tbody>
            {satirlar.map((r) => {
              const marj = urunMarji(r);
              return (
                <tr key={r.stok_kodu} data-testid={`cok-satan-satir-${r.stok_kodu}`} className="border-t border-border">
                  <td className="py-1 pr-2 text-ink-800 truncate max-w-0 w-full">
                    {onUrunSec ? (
                      <button
                        type="button"
                        data-testid={`cok-satan-detay-${r.stok_kodu}`}
                        onClick={() => onUrunSec({ stokKodu: String(r.stok_kodu), urunAdi: String(r.urun_adi) })}
                        className="focus-ring block w-full truncate text-left hover:underline underline-offset-2"
                      >
                        {r.urun_adi}
                      </button>
                    ) : (
                      r.urun_adi
                    )}
                  </td>
                  {ayrintili && <td className="py-1 pl-2 text-right tnum text-ink-500 whitespace-nowrap">{miktarMetni(sayi(r.miktar))}</td>}
                  <td className="py-1 pl-2 text-right tnum text-ink-700 whitespace-nowrap">{fmtTRY(sayi(r.ciro))}</td>
                  {ayrintili && <td className="py-1 pl-2 text-right tnum text-ink-700 whitespace-nowrap">{fmtTRY(sayi(r.kar))}</td>}
                  <td className={`py-1 pl-2 text-right tnum whitespace-nowrap ${marj !== null && marjGoruntudeNegatifMi(marj) ? 'text-neg-700' : 'text-ink-500'}`}>
                    {marj === null ? '—' : oranMetni(marj)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </SectionCard>
    </div>
  );
}
